<template>
  <div class="select-filter fds">
    <t-row class="filter pt-1 pb-2">
      <t-column class="row-title d-flex flex-row align-items-center justify-content-start" size="100%">
        <prefix class="mt-1 mr-2">
          <input
            type="checkbox"
            :checked="allFilteredEntriesSelected || allSeenEntriesSelected"
            @input="setSelectAllSeenEntries"
            id="select-all-entries-checkbox"
          >
          <label for="select-all-entries-checkbox" class="sr-only">Select All Entries</label>
        </prefix>
        <p class="mr-3 flex-shrink-0" v-if="allFilteredEntriesSelected">
          All Filtered Entries selected
        </p>
        <p class="mr-3 flex-shrink-0" v-else>
          {{ 'row' | pluralize(selectedEntries.length)}} selected
        </p>
        <div class="dropdown mr-3">
          <button class="dropdown-toggle arrow btn xs action-btn green bulk-workflow-actions-options"
                  data-toggle="dropdown"
                  aria-haspopup="true"
                  id="bulkActions"
                  aria-expanded="false"
                  :disabled="isDisabled">
            Actions
          </button>
          <ul class="dropdown-menu" aria-labelledby="bulkActions">
            <li v-if="selectedEntries.length > 0 || allFilteredEntriesSelected ">
              <button v-for="action in availableBulkActions" class="dropdown-item" :key="action.id" @click="actionRequested(action)">
                {{action.label_title || "(Untitled Action)"}}
              </button>
              <button @click="deselectAllEntries" class="dropdown-item">Deselect All</button>
            </li>
          </ul>
        </div>
        <div v-if="allSeenEntriesSelected && !allFilteredEntriesSelected">
          <div>
            All {{selectedEntries.length}} visible entries on this page are selected.
            <a @click="setSelectAllFilteredEntries" class="btn-link">
              Select all entries that match this filter.
            </a>
          </div>
        </div>
      </t-column>
    </t-row>

    <object-model-action-custom-notification
      bulk
      :state="modalStateFor(vueModalID.objectModelActionCustomNotification)"
      :index="vueModalID.objectModelActionCustomNotification"
      @toggle-modal="hideCustomNotificationModal"
      @send="notificationComposed($event)"
    />

    <confirmation-modal
      v-if="modalStateFor(vueModalID.confirmation).isVisible"
      :state="modalStateFor(vueModalID.confirmation)"
      @toggle-modal="confirmationModalToggled"
      :overrideConfig="confirmationModalConfig"
    />
  </div>
</template>

<script lang="ts">
  //this component is used by both the list view and the detail view
  import Vue from "vue";
  import bulkActionMixin from "~/mixins/bulkActionMixin";
  import Pluralizeable from "~/mixins/pluralizeable"
  import ListView from "~/models/listViews/listView";
  import PublishedListView from "~/models/listViews/publishedListView";
  import ConfirmationModal from "~/components/modals/confirmationModal.vue";
  import { IConfirmModalConfig } from "../../../common/interfaces/modalInterfaces";
  import ObjectModelActionCustomNotification from "./object-model-action-custom-notification.vue";
  import {ObjectModelActionType} from "~/enums/objectModelActionType";
  import listViewAvailableBulkActionsMixin from '~/mixins/listViewAvailableBulkActionsMixin';

  export default Vue.extend({
    props: {
      listView: {
        type: Object as () => ListView | PublishedListView,
        required: true
      },
    },
    components: {
      ConfirmationModal,
      ObjectModelActionCustomNotification,
    },
    mixins: [
      bulkActionMixin,
      listViewAvailableBulkActionsMixin,
      Pluralizeable,
    ],
    data: function () {
      return {
        requestedAction: null,
        notificationDetails: null,
      };
    },
    computed: {
      numSelectedEntries() {
        return this.bulkActionStore.selectedEntries.length
      },
      isDisabled() {
        return this.selectedEntries.length === 0
      },
      allFilteredEntriesSelected(): boolean {
        return this.bulkActionStore.allFilteredEntriesSelected
      },
      confirmationModalConfig(): Partial<IConfirmModalConfig> {
        let confirmationMessage

        if (this.allFilteredEntriesSelected) {
          confirmationMessage = `You are about to take the action, ${this.requestedAction.label_title}, for all records that match your current filter settings. Are you sure you want to proceed?`
        } else {
          const records = (this.numSelectedEntries === 1) ? 'record' : 'records'
          confirmationMessage = `You are about to take the action, ${this.requestedAction.label_title}, for ${this.numSelectedEntries} ${records}. Are you sure you want to proceed?`
        }

        return {
          confirmationMessage,
        }
      },
      isNotificationAction() {
        return this.requestedAction.action_type === ObjectModelActionType.CustomNotification
      }
    },
    methods: {
      actionRequested(action) {
        this.requestedAction = action

        if (this.isNotificationAction) {
          this.showModal(this.vueModalID.objectModelActionCustomNotification)
        } else {
          this.showConfirmationModal()
        }
      },
      hideCustomNotificationModal(): void {
        this.hideModal(this.vueModalID.objectModelActionCustomNotification);
      },
      notificationComposed(notificationDetails) {
        this.notificationDetails = notificationDetails
        this.hideCustomNotificationModal()
        this.showConfirmationModal()
      },
      showConfirmationModal() {
        this.showModal(this.vueModalID.confirmation);
      },
      confirmationModalToggled(data) {
        this.hideModal(this.vueModalID.confirmation);
        const outcome = $(data.event.currentTarget).data('action');
        if (outcome === 'affirm') {
          this.createBulkActionJob();
        }
      },
      async createBulkActionJob(action) {
        let createJobParams: any = {
          listView: this.listView,
          action: this.requestedAction,
        }

        if (this.allFilteredEntriesSelected) {
          createJobParams.listViewFilterQuery = this.listView.filterQuery
        }

        if (this.isNotificationAction) {
          createJobParams.actionParams = this.notificationDetails
        }

        try {
          await this.$store.dispatch('bulkActionStore/createJob', createJobParams)
          this.$store.commit(
            'flashStore/set', {
              flash: {
                success: 'Bulk action submitted successfully. You will be notified when the bulk action is complete. Refresh the page upon completion.',
              },
            },
          );
        } catch {
          this.$store.commit('flashStore/set', {flash: {alert: 'Bulk action could not be submitted'}});
        }
      },
    },
  });
</script>